import React, { useState, useEffect } from "react";
import { Table } from "antd";
import { useCustomer } from "_contexts/useCustomer";
import { useApolloClient } from "@apollo/client";
import { portalPayments } from "_graphql/queries/portal";
import dayjs from "dayjs";
import "./PaymentSchedule.scss";

function PaymentSchedule() {
  const client = useApolloClient();
  const { applicationId } = useCustomer();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);

  const fetchPayments = async () => {
    if (!applicationId) return;

    try {
      const queryData = await client.query({
        variables: { id: applicationId },
        query: portalPayments,
      });

      setPayments(queryData?.data?.portalPayments?.data || []);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [applicationId]);

  // Calculate summary stats
  const paidPayments = payments.filter(
    (p) => p.status === "paid" || p.status === "success"
  );
  const totalPayments = payments.length;
  const paidCount = paidPayments.length;
  const totalAmount = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const paidAmount = paidPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const remainingAmount = totalAmount - paidAmount;

  // Calculate progress percentage
  const progress = totalPayments > 0 ? (paidCount / totalPayments) * 100 : 0;

  // Calculate timeline labels
  const firstPayment =
    payments.length > 0 ? dayjs(parseInt(payments[0]?.first_attempt)) : dayjs();
  const lastPayment =
    payments.length > 0
      ? dayjs(parseInt(payments[payments.length - 1]?.next_attempt))
      : dayjs();
  const totalMonths = lastPayment.diff(firstPayment, "month");
  const midMonths = Math.floor(totalMonths / 2);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (date) => {
    return dayjs(parseInt(date)).format("MMM. D, YYYY");
  };

  const getStatusDisplay = (payment) => {
    // Handle actual payment statuses first
    if (payment.status === "paid" || payment.status === "success") {
      return { text: "Paid", type: "paid" };
    }

    if (payment.status === "failed") {
      return { text: "Failed", type: "failed" };
    }

    if (payment.status === "return") {
      return { text: "Returned", type: "failed" };
    }

    if (payment.status === "processing") {
      return { text: "Processing", type: "processing" };
    }

    if (payment.status === "cancelled") {
      return { text: "Cancelled", type: "cancelled" };
    }

    // For "open" status, calculate due date info
    const dueDate = dayjs(parseInt(payment.next_attempt));
    const today = dayjs().startOf("day");
    const daysUntil = dueDate.diff(today, "day");

    if (daysUntil < 0) {
      return { text: "Overdue", type: "overdue" };
    } else if (daysUntil === 0) {
      return { text: "Due today", type: "due" };
    } else if (daysUntil <= 30) {
      return {
        text: `Due in ${daysUntil} day${daysUntil > 1 ? "s" : ""}`,
        type: "due",
      };
    } else {
      const months = Math.floor(daysUntil / 30);
      return {
        text: `Due in ${months} month${months > 1 ? "s" : ""}`,
        type: "future",
      };
    }
  };

  const columns = [
    {
      title: "Payment #",
      dataIndex: "order",
      key: "order",
      width: 170,
      render: (order) => (
        <span className="payment-number">Payment #{order}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 170,
      render: (_, record) => {
        const status = getStatusDisplay(record);
        return (
          <span className={`payment-status ${status.type}`}>{status.text}</span>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "next_attempt",
      key: "next_attempt",
      width: 170,
      render: (date) => (
        <span className="payment-date">{formatDate(date)}</span>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 170,
      render: (amount) => (
        <span className="payment-amount">{formatCurrency(amount)}</span>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="payment-schedule-page">
        <div className="dashboard-loading">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="payment-schedule-page">
      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title">Payment Schedule</h1>
        <p className="page-subtitle">
          View your complete payment timeline. Track due dates, amounts, and
          progress towards program completion.
        </p>
      </div>

      {/* Combined Card: Program Progress + Table */}
      <div className="schedule-card">
        {/* Program Progress Section */}
        <div className="program-progress-section">
          <h2 className="section-title">Program Progress</h2>
          <div className="progress-bar-section">
            <div className="progress-bar-background">
              <div
                className="progress-bar-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="progress-labels">
            <div className="progress-label">
              <div className="label-tick" />
              <span>0</span>
            </div>
            <div className="progress-label">
              <div className="label-tick" />
              <span>{midMonths} mo.</span>
            </div>
            <div className="progress-label">
              <div className="label-tick" />
              <span>{totalMonths} mo.</span>
            </div>
          </div>

          <div className="progress-stats">
            <div className="stat-item">
              <span className="stat-label">Total</span>
              <span className="stat-value">{formatCurrency(totalAmount)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Paid</span>
              <span className="stat-value">{formatCurrency(paidAmount)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Remaining</span>
              <span className="stat-value">
                {formatCurrency(remainingAmount)}
              </span>
            </div>
          </div>
        </div>

        {/* Payment Schedule Table */}
        <div className="schedule-table-container">
          <Table
            columns={columns}
            dataSource={payments}
            rowKey="id"
            pagination={false}
            showHeader={true}
          />
        </div>
      </div>
    </div>
  );
}

export default PaymentSchedule;
